import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, CardBody, Card } from 'reactstrap';
import { Tooltip, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import HubIcon from '@mui/icons-material/OtherHouses';
import BadgeIcon from '@mui/icons-material/Badge';
import SettingsIcon from '@mui/icons-material/Settings';
import TimeToLeaveIcon from '@mui/icons-material/TimeToLeave';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';
import { getSubscriptionStatus } from 'src/utils/validationUtils';
import { format } from 'date-fns';

const VEHICLE_COLUMNS = [
  {
    key: 'plateNo',
    header: 'Plate No.',
    sortable: true,
  },
  {
    key: 'vehicleUniqueId',
    header: 'Vehicle ID',
    sortable: true,
  },
  {
    key: 'modelName',
    header: 'Model',
    sortable: true,
  },
  {
    key: 'hubName',
    header: 'Hub',
    sortable: true,
  },
];

const VehicleTableView = ({
  reactTableData,
  isNoRecordFound,
  onAdd,
  onEdit,
  onAssignHub
}) => {
  const rows = reactTableData?.tableRowsData ?? [];

  const [selectedVehicle, setSelectedVehicle] = useState(
    rows.length > 0 ? rows[0] : null
  );

  const getExpiryBadge = (dateValue) => {
    if (!dateValue) return <span className="badge bg-secondary">N/A</span>;
    try {
      const formattedDate = format(new Date(dateValue), 'yyyy-MM-dd');
      const status = getSubscriptionStatus(formattedDate);

      let bg = '#10b981'; // green
      let text = '#ffffff';
      let label = 'Valid';

      if (status === 'expired') {
        bg = '#ef4444'; // red
        label = 'Expired';
      } else if (status === 'expiring-this-month') {
        bg = '#eab308'; // yellow
        label = 'Expiring This Month';
      } else if (status === 'expiring-next-month') {
        bg = '#f97316'; // orange
        label = 'Expiring Next Month';
      } else {
        label = 'Active';
      }

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span className="fw-semibold text-dark">{formattedDate}</span>
          <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '0.7rem',
            fontWeight: 700,
            backgroundColor: bg,
            color: text,
            width: 'fit-content'
          }}>
            {label.toUpperCase()}
          </span>
        </div>
      );
    } catch (e) {
      return <span className="badge bg-secondary">INVALID DATE</span>;
    }
  };

  return (
    <Col md={12} lg={12} style={{ marginTop: '20px' }}>
      <Row>
        {/* ── Left: Master List ───────────────────────────── */}
        <Col md={7} lg={7}>
          <RadioSelectTable
            rows={rows}
            columns={VEHICLE_COLUMNS}
            rowKey="vehicleId"
            selectedKey={selectedVehicle?.vehicleId}
            onSelect={setSelectedVehicle}
            searchKeys={['plateNo', 'vehicleUniqueId', 'modelName', 'makerName', 'hubName']}
            searchPlaceholder="Search by Plate, ID, Model, Make..."
            sortKey="plateNo"
            headerTitle="Vehicle List"
            onAdd={onAdd}
            pageSizes={[5, 10, 20, 50]}
          />
        </Col>

        {/* ── Right: Detail Panel ─────────────────────────── */}
        <Col md={5} lg={5}>
          <Card style={{
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
            minHeight: '520px',
            background: '#ffffff',
            overflow: 'hidden',
          }}>
            {selectedVehicle ? (
              <>
                {/* Header Banner */}
                <div style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  padding: '24px 20px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <DirectionsCarIcon style={{ color: '#ffffff', fontSize: '2.5rem', opacity: 0.9 }} />
                      <div>
                        <h4 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: '#ffffff' }}>
                          Plate: {selectedVehicle.plateNo}
                        </h4>
                        <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
                          ID: {selectedVehicle.vehicleUniqueId}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span
                        className={`badge ${selectedVehicle.status ? 'bg-success' : 'bg-danger'}`}
                        style={{ fontSize: '0.75rem', fontWeight: 700 }}
                      >
                        {selectedVehicle.status ? 'ACTIVE' : 'IN-ACTIVE'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Details Body */}
                <CardBody style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* Vehicle Specifications */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Vehicle Details
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Make / Manufacturer:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <TimeToLeaveIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedVehicle.makerName || '—'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Model Name:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <SettingsIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedVehicle.modelName || '—'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Vehicle Type:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <BadgeIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedVehicle.vehicleType || '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Registration Expiry Status */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Registration Status
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted small d-flex align-items-center gap-1">
                            <CalendarTodayIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            Registration Expiry:
                          </span>
                          {getExpiryBadge(selectedVehicle.vehicleRegistrationExpiryDate)}
                        </div>
                      </div>
                    </div>

                    {/* Hub Assignment */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Hub Allocations
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted small">Assigned Hub:</span>
                          {selectedVehicle.hubName ? (
                            <span className="badge bg-primary text-wrap" style={{ fontSize: '0.78rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px' }}>
                              <HubIcon style={{ fontSize: '14px' }} />
                              {selectedVehicle.hubName}
                            </span>
                          ) : (
                            <span className="badge bg-secondary" style={{ fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px' }}>
                              <HubIcon style={{ fontSize: '14px' }} />
                              Unassigned
                            </span>
                          )}
                        </div>


                      </div>
                    </div>

                  </div>
                </CardBody>
              </>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '40px', textAlign: 'center', minHeight: '520px',
              }}>
                <div style={{ fontSize: '3.5rem', color: '#cbd5e1', marginBottom: '16px' }}>🚗</div>
                <h5 className="fw-bold text-muted">No Vehicle Selected</h5>
                <p className="text-muted small">Select a vehicle from the list to view details.</p>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </Col>
  );
};

VehicleTableView.propTypes = {
  reactTableData: PropTypes.shape({
    tableRowsData: PropTypes.arrayOf(PropTypes.shape()),
  }),
  isNoRecordFound: PropTypes.bool,
  onAdd: PropTypes.func,
  onEdit: PropTypes.func,
  onAssignHub: PropTypes.func,
};

export default VehicleTableView;
